/**
 * English: Task run inbox — lists persisted task summaries from PostgreSQL task_runs.
 * Thai: กล่องงาน task run — แสดงสรุปงานที่บันทึกจากตาราง task_runs ใน PostgreSQL
 */
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  ArrowPathIcon,
  InboxIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  QueueListIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useTaskRuns } from '../hooks/useTaskRuns';
import StatusBadge from '../components/shared/StatusBadge';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import PageShell from '../components/layout/PageShell';
import PageHeader from '../components/layout/PageHeader';
import { TaskRun } from '../types';
import { useAuth } from '../context/AuthContext';
import {
  inboxModeForTaskRun,
  isTaskRunVisibleInInbox,
  modeFilterCount,
  normalizeTaskRunMode,
  TASK_INBOX_FILTER_MODES,
  taskRunMatchesModeFilter,
} from '../utils/taskRunMode';

function taskModeLabel(mode: string, t: TFunction): string {
  const normalized = normalizeTaskRunMode(mode);
  const key = `tasks.modes.${normalized}`;
  const translated = t(key);
  return translated === key ? normalized || mode : translated;
}

const MODE_COLOR: Record<string, string> = {
  ask: '#3b82f6',
  insight: '#3b82f6',
  insights: '#3b82f6',
  control: '#f59e0b',
  chat: '#10b981',
  employee: '#f59e0b',
  master: '#a855f7',
  agent: '#6366f1',
};

type StatusFilter = 'all' | 'completed' | 'error' | 'pending';

function statusVariant(status: string): 'success' | 'error' | 'info' | 'muted' {
  if (status === 'completed') return 'success';
  if (status === 'error') return 'error';
  if (status === 'pending') return 'info';
  return 'muted';
}

function relativeTime(dateStr: string, t: TFunction): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('tasks.relativeJustNow');
  if (mins < 60) return t('tasks.relativeMinutes', { n: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('tasks.relativeHours', { n: hrs });
  const days = Math.floor(hrs / 24);
  return t('tasks.relativeDays', { n: days });
}

function durationLabel(start: string, end: string): string | null {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 100) / 10;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s - m * 60);
  return r ? `${m}m ${r}s` : `${m}m`;
}

function dayBucket(dateStr: string): 'today' | 'yesterday' | 'earlier' {
  const d = new Date(dateStr);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 86_400_000;
  const t = d.getTime();
  if (t >= startOfToday) return 'today';
  if (t >= startOfYesterday) return 'yesterday';
  return 'earlier';
}

function bucketLabel(bucket: 'today' | 'yesterday' | 'earlier', t: TFunction): string {
  const keys = {
    today: 'tasks.bucketToday',
    yesterday: 'tasks.bucketYesterday',
    earlier: 'tasks.bucketEarlier',
  } as const;
  return t(keys[bucket]);
}

interface TaskRowProps {
  run: TaskRun;
}

function TaskRow({ run }: TaskRowProps) {
  const { t } = useTranslation();
  const displayMode = inboxModeForTaskRun(run);
  const modeColor = displayMode ? (MODE_COLOR[displayMode] ?? MODE_COLOR[run.mode ?? ''] ?? '#6b7280') : '#6b7280';
  const modeLabel = displayMode ? taskModeLabel(displayMode, t) : null;
  const duration = durationLabel(run.created_at, run.finished_at);
  const text = run.prompt || run.summary;

  return (
    <Link
      to={`/tasks/${run.task_id}`}
      className="bg-app-surface border border-app-border rounded-xl overflow-hidden flex hover:border-app-accent/60 hover:shadow-sm transition-all group"
    >
      {/* Left color accent bar */}
      <div className="w-1 shrink-0" style={{ backgroundColor: modeColor }} />

      {/* Content */}
      <div className="px-4 py-3 flex items-center gap-4 flex-1 min-w-0">
        <div className="min-w-0 flex-1 space-y-1">
          <p
            className="text-sm text-app-text truncate leading-snug"
            title={text || undefined}
          >
            {text ?? <span className="italic text-app-text-dim">{t('common.noPrompt')}</span>}
          </p>
          {run.prompt && run.summary && run.summary !== run.prompt && (
            <p className="text-xs text-app-text-dim truncate" title={run.summary}>
              → {run.summary}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 text-xs text-app-text-dim">
            {modeLabel && (
              <span
                className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide"
                style={{ backgroundColor: `${modeColor}22`, color: modeColor }}
              >
                {modeLabel}
              </span>
            )}
            {run.station_id && (
              <span className="font-mono bg-app-muted px-1.5 py-0.5 rounded text-app-text-sec">
                {run.station_id}
              </span>
            )}
            <span>{run.message_count} msg{run.message_count !== 1 ? 's' : ''}</span>
            {duration && (
              <>
                <span className="text-app-border">·</span>
                <span title={t('common.duration')}>⏱ {duration}</span>
              </>
            )}
            <span className="text-app-border">·</span>
            <span title={new Date(run.finished_at).toLocaleString()}>
              {relativeTime(run.finished_at, t)}
            </span>
          </div>
        </div>

        {/* Right: status + id */}
        <div className="flex items-center gap-3 shrink-0">
          <StatusBadge variant={statusVariant(run.status)} dot>
            {run.status}
          </StatusBadge>
          <span className="text-xs text-app-text-dim font-mono whitespace-nowrap opacity-60 group-hover:opacity-100 transition-opacity">
            {run.task_id.slice(0, 8)}…
          </span>
        </div>
      </div>
    </Link>
  );
}

interface StatCardProps {
  label: string;
  value: number;
  Icon: typeof QueueListIcon;
  color: string;
  active: boolean;
  onClick: () => void;
}

function StatCard({ label, value, Icon, color, active, onClick }: StatCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`bg-app-surface border rounded-xl p-4 flex items-center gap-3 text-left transition-all ${
        active
          ? 'border-app-accent ring-2 ring-app-accent/20'
          : 'border-app-border hover:border-app-accent/40'
      }`}
    >
      <Icon className={`w-5 h-5 ${color} shrink-0`} />
      <div>
        <p className={`text-xl font-bold tabular-nums ${color}`}>{value}</p>
        <p className="text-xs text-app-text-dim">{label}</p>
      </div>
    </button>
  );
}

export default function TaskInboxPage() {
  const { t } = useTranslation();
  const { userOrgName } = useAuth();
  const { runs: rawRuns, loading, error, refresh } = useTaskRuns(200, { orgName: userOrgName || undefined });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [modeFilter, setModeFilter] = useState<string>('all');
  const [stationFilter, setStationFilter] = useState<string>('all');

  const runs = useMemo(
    () => rawRuns.filter((r) => isTaskRunVisibleInInbox(r.mode)),
    [rawRuns],
  );

  const stations = useMemo(() => {
    const s = new Set<string>();
    runs.forEach(r => { if (r.station_id) s.add(r.station_id); });
    return Array.from(s).sort();
  }, [runs]);


  const stats = useMemo(() => ({
    total:     runs.length,
    completed: runs.filter(r => r.status === 'completed').length,
    errors:    runs.filter(r => r.status === 'error').length,
    pending:   runs.filter(r => r.status === 'pending').length,
  }), [runs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return runs.filter(r => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (modeFilter !== 'all' && !taskRunMatchesModeFilter(r, modeFilter)) return false;
      if (stationFilter !== 'all' && r.station_id !== stationFilter) return false;
      if (q) {
        const hay = `${r.prompt ?? ''} ${r.summary ?? ''} ${r.task_id}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [runs, statusFilter, modeFilter, stationFilter, search]);

  const grouped = useMemo(() => {
    const buckets: Record<'today' | 'yesterday' | 'earlier', TaskRun[]> = {
      today: [], yesterday: [], earlier: [],
    };
    filtered.forEach(r => buckets[dayBucket(r.finished_at)].push(r));
    return buckets;
  }, [filtered]);

  const hasFilters =
    search.trim() !== '' ||
    statusFilter !== 'all' ||
    modeFilter !== 'all' ||
    stationFilter !== 'all';

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setModeFilter('all');
    setStationFilter('all');
  };

  return (
    <PageShell>
      <PageHeader
        title={t('tasks.title')}
        description={
          userOrgName
            ? t('tasks.descriptionOrg', { org: userOrgName })
            : t('tasks.descriptionDefault')
        }
      >
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-app-accent border border-app-border hover:bg-app-muted disabled:opacity-50 transition-colors"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {t('common.refresh')}
        </button>
      </PageHeader>

      {/* Clickable stats — also act as status filter */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label={t('tasks.total')}
          value={stats.total}
          Icon={QueueListIcon}
          color="text-app-text"
          active={statusFilter === 'all'}
          onClick={() => setStatusFilter('all')}
        />
        <StatCard
          label={t('tasks.completed')}
          value={stats.completed}
          Icon={CheckCircleIcon}
          color="text-app-success"
          active={statusFilter === 'completed'}
          onClick={() => setStatusFilter(statusFilter === 'completed' ? 'all' : 'completed')}
        />
        <StatCard
          label={t('tasks.errors')}
          value={stats.errors}
          Icon={ExclamationCircleIcon}
          color="text-app-error"
          active={statusFilter === 'error'}
          onClick={() => setStatusFilter(statusFilter === 'error' ? 'all' : 'error')}
        />
        <StatCard
          label={t('tasks.pending')}
          value={stats.pending}
          Icon={ClockIcon}
          color="text-app-accent"
          active={statusFilter === 'pending'}
          onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
        />
      </div>

      {/* Sticky filter bar */}
      <div className="sticky top-0 z-10 -mx-1 px-1 py-2 bg-app-bg/90 backdrop-blur supports-[backdrop-filter]:bg-app-bg/70 border-b border-app-border/50 space-y-2">
        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-app-text-dim" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('tasks.searchPlaceholder')}
            className="w-full bg-app-surface border border-app-border rounded-lg pl-9 pr-9 py-2 text-sm text-app-text placeholder:text-app-text-dim focus:outline-none focus:border-app-accent"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-app-muted text-app-text-dim hover:text-app-text"
              title={t('common.clearSearch')}
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Mode pills */}
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setModeFilter('all')}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                modeFilter === 'all'
                  ? 'bg-app-accent text-white'
                  : 'bg-app-muted text-app-text-sec hover:bg-app-border'
              }`}
            >
              {t('tasks.allModes')}
            </button>
            {TASK_INBOX_FILTER_MODES.map((m) => {
              const c = MODE_COLOR[m] ?? '#6b7280';
              const active = modeFilter === m;
              const count = modeFilterCount(runs, m);
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setModeFilter(active ? 'all' : m)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                    active ? 'text-white' : 'text-app-text-sec hover:bg-app-border bg-app-muted border-transparent'
                  }`}
                  style={active ? { backgroundColor: c, borderColor: c } : undefined}
                  title={count > 0 ? `${count}` : undefined}
                >
                  {taskModeLabel(m, t)}
                  {count > 0 ? (
                    <span className={`ml-1 tabular-nums ${active ? 'opacity-90' : 'opacity-70'}`}>
                      {count}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          {/* Station dropdown */}
          {stations.length > 1 && (
            <select
              value={stationFilter}
              onChange={e => setStationFilter(e.target.value)}
              className="text-xs bg-app-surface border border-app-border rounded-lg px-2.5 py-1.5 text-app-text focus:outline-none focus:border-app-accent"
            >
              <option value="all">{t('tasks.allStations')}</option>
              {stations.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}

          {/* Right side: count + clear */}
          <div className="ml-auto flex items-center gap-2">
            {hasFilters && (
              <span className="text-xs text-app-text-dim tabular-nums">
                {t('common.of', { filtered: filtered.length, total: runs.length })}
              </span>
            )}
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md text-app-text-dim hover:text-app-text hover:bg-app-muted transition-colors"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
                {t('common.clear')}
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-app-error/10 border border-app-error/30 rounded-xl px-4 py-3 text-sm text-app-error">
          {error}
        </div>
      )}

      {/* Task list */}
      {loading && runs.length === 0 ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-app-surface border border-app-border rounded-xl flex flex-col items-center justify-center py-12 gap-3 text-app-text-dim">
          <InboxIcon className="w-8 h-8" />
          <p className="text-sm">
            {runs.length === 0 ? t('tasks.noHistory') : t('tasks.noMatch')}
          </p>
          {runs.length === 0 ? (
            <p className="text-xs text-center max-w-xs">
              {t('tasks.emptyHint')}{' '}
              <Link to="/chat-test" className="text-app-accent hover:underline">{t('tasks.emptyChatLink')}</Link>
              {t('tasks.emptyHintEnd')}
            </p>
          ) : (
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs text-app-accent hover:underline"
            >
              {t('tasks.clearFilters')}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {(['today', 'yesterday', 'earlier'] as const).map(b => (
            grouped[b].length > 0 && (
              <section key={b} className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-app-text-dim px-1">
                  {bucketLabel(b, t)}
                  <span className="ml-2 font-normal normal-case tracking-normal text-app-text-dim/70">
                    {grouped[b].length}
                  </span>
                </h3>
                <div className="space-y-2">
                  {grouped[b].map(run => (
                    <TaskRow key={run.task_id} run={run} />
                  ))}
                </div>
              </section>
            )
          ))}
        </div>
      )}
    </PageShell>
  );
}
